import { MedicalKnowledgeEntry } from "./types";
import { MedicalSearchService } from "./medicalSearch";

export class MedicalAnalysisService {
  // Fast enhanced analysis - optimized for speed
  static async getEnhancedAnalysis(
    symptoms: string[],
    severity: number,
    duration: string
  ): Promise<{
    relevantResearch: MedicalKnowledgeEntry[];
    clinicalGuidelines: MedicalKnowledgeEntry[];
    drugSafetyInfo: MedicalKnowledgeEntry[];
    evidenceLevel: string;
    recommendations: string[];
  }> {
    try {
      console.log('Fast enhanced medical analysis for:', { symptoms, severity, duration });

      // Parallel fast queries
      const [relevantResearch, clinicalGuidelines, drugSafetyInfo] = await Promise.all([
        MedicalSearchService.searchRelevantKnowledge(symptoms, [], ['clinical_research', 'diagnosis'], 15),
        MedicalSearchService.getClinicalGuidelines(),
        MedicalSearchService.getDrugSafetyInfo()
      ]);

      // Quick evidence level determination
      const evidenceLevel = this.quickEvidenceLevel(relevantResearch, clinicalGuidelines);

      // Generate fast recommendations
      const recommendations = this.generateFastRecommendations(symptoms, severity, duration, relevantResearch);

      console.log('Fast enhanced analysis complete:', {
        researchCount: relevantResearch.length,
        guidelinesCount: clinicalGuidelines.length,
        evidenceLevel
      });

      return {
        relevantResearch,
        clinicalGuidelines,
        drugSafetyInfo,
        evidenceLevel,
        recommendations
      };
    } catch (error) {
      console.error('Error in getEnhancedAnalysis:', error);
      return {
        relevantResearch: [],
        clinicalGuidelines: [],
        drugSafetyInfo: [],
        evidenceLevel: 'medium',
        recommendations: ['Medical evaluation recommended due to symptom severity']
      };
    }
  }

  // Quick evidence level assessment
  private static quickEvidenceLevel(research: MedicalKnowledgeEntry[], guidelines: MedicalKnowledgeEntry[]): string {
    const allEntries = [...research, ...guidelines];
    
    if (allEntries.length === 0) return 'low';

    const highEvidence = allEntries.filter(e => e.evidence_level === 'high').length;
    const ratio = highEvidence / allEntries.length;

    if (ratio >= 0.4) return 'high';
    if (ratio >= 0.2) return 'medium';
    return 'low';
  }

  // Fast recommendation generation
  private static generateFastRecommendations(
    symptoms: string[],
    severity: number,
    duration: string,
    research: MedicalKnowledgeEntry[]
  ): string[] {
    const recommendations = [];

    // Severity-based recommendations
    if (severity >= 8) {
      recommendations.push('URGENT medical attention required - seek immediate care');
    } else if (severity >= 6) {
      recommendations.push('Medical evaluation recommended within 24-48 hours');
    } else {
      recommendations.push('Routine medical consultation recommended');
    }

    // Duration-based recommendations
    if (duration.includes('acute') || duration.includes('hours')) {
      recommendations.push('Acute onset symptoms warrant prompt medical evaluation');
    }

    // Evidence-based recommendations
    if (research.length >= 5) {
      recommendations.push(`Strong evidence base (${research.length} studies) supports professional medical assessment`);
    }

    // Symptom-specific recommendations
    const emergentSymptoms = ['fever', 'severe', 'difficulty', 'chest', 'breathing'];
    const hasEmergentSymptoms = symptoms.some(symptom => 
      emergentSymptoms.some(emergent => symptom.toLowerCase().includes(emergent))
    );

    if (hasEmergentSymptoms) {
      recommendations.push('Presenting symptoms may indicate conditions requiring medical evaluation');
    }

    return recommendations.slice(0, 6);
  }
}